/**
 * The calibration bench — what this rubric will actually do, before it goes
 * live. PRD §1.4's Tier 1 claim 2.
 *
 * Visual Reference: `design/screens/editor-qualify.html`, the **Calibration
 * bench** block.
 *
 * IT LEADS WITH THE SENTENCE. PRD §1.9: "Every screen leads with a
 * plain-language sentence, not a number… This holds on… the calibration
 * bench." The sentence is not built here — `benchSentence` in
 * `packages/schema/src/calibrate.ts` is the one function that writes it, so a
 * component that wanted to lead with a figure would have to go out of its way
 * to make one. The histogram is the evidence BENEATH it.
 *
 * NOTHING HERE COMPUTES A STATISTIC. The range, the interquartile statement,
 * the per-band shares, the stated unknown ranges and the buckets all arrive
 * from the Story 2.4 sampler through `POST …/calibrate`. A bench that bucketed
 * its own scores would be a second opinion about the shape of the thing it
 * exists to describe.
 *
 * Story 7.2.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { BandShare, Calibration, HistogramBucket, UnknownCriterion } from '@leadgible/schema'
import { ApiFailure, apiPost } from '../lib/api'
import { useShell } from '../shell/workspace'
import { Callout } from '../ui/Callout'
import { Skeleton } from '../ui/Skeleton'
import { COMPOSED as DERIVED_COMPOSED } from './derived-copy'
import { useEditor } from './store'

type Bench = Calibration & { sentence: string }

/**
 * How long the editor waits after an edit before re-calibrating.
 *
 * Longer than the draft's 1200ms autosave on purpose: a save is what makes the
 * work safe and should feel immediate, and a re-sample is a second opinion the
 * operator did not ask for mid-keystroke. Waiting until they have stopped
 * moving a weight is what stops the number flickering under their hand.
 */
const DEBOUNCE_MS = 1500

/** The tones a bar can wear. `null` is a score no band covers — see below. */
const BAR_TONE: Record<'nurture' | 'review' | 'hot', string> = {
  nurture: 'bg-mdx-nurture/40',
  review: 'bg-mdx-review/70',
  hot: 'bg-mdx-hot/70',
}

/** The evidence rows' marks, drawn as the export draws them. */
const MARK_TONE = {
  range: 'bg-mdx-nurture text-mdx-white',
  spread: 'bg-mdx-review text-mdx-white',
  unknown: 'bg-mdx-line text-mdx-slate',
} as const

export function Bench({ funnelId }: { funnelId: string }) {
  const { workspace } = useShell()
  const workspaceId = workspace?.id ?? null
  const funnel = useEditor((s) => s.funnel)

  const [bench, setBench] = useState<Bench | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  /**
   * The rubric AS A STRING, so the effect re-runs when the rubric changes and
   * not when anything else about the draft does. Renaming the funnel or moving
   * a block does not change what the rubric will do, and re-sampling for it
   * would spend a request to redraw the same bars.
   */
  const rubricKey = funnel ? JSON.stringify(funnel.rubric) : ''

  // The request in flight, so a slower earlier answer cannot overwrite a
  // faster later one — the classic out-of-order render, which on this screen
  // would show the operator statistics for a rubric they have already changed.
  const latest = useRef(0)

  const run = useCallback(async () => {
    if (!workspaceId || !rubricKey) return
    const ticket = ++latest.current
    setPending(true)
    setError(null)
    try {
      const next = await apiPost<Bench>(`/v1/w/${workspaceId}/funnels/${funnelId}/calibrate`, {})
      if (ticket === latest.current) setBench(next)
    } catch (e) {
      if (ticket !== latest.current) return
      setError(
        e instanceof ApiFailure
          ? e.message
          : "We couldn't calibrate this rubric. Check your connection and try again.",
      )
    } finally {
      if (ticket === latest.current) setPending(false)
    }
  }, [workspaceId, funnelId, rubricKey])

  useEffect(() => {
    const timer = setTimeout(() => void run(), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [run])

  return (
    <section className="flex flex-col gap-mdx-24 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-28 shadow-mdx-card">
      <div className="flex flex-col gap-mdx-10">
        <span className="text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-slate">
          Calibration bench
        </span>
        {/*
          THE SENTENCE, and it is an `h2` because it is the heading of what
          follows rather than a caption on it. `--mdx-text-verdict` is the same
          size the lead drawer's Verdict uses, which is the point: the product
          makes one kind of claim and it looks the same everywhere.
        */}
        {bench ? (
          <h2 className="m-0 text-balance text-mdx-verdict font-mdx-bold leading-mdx-verdict tracking-mdx-verdict">
            {bench.sentence}
          </h2>
        ) : (
          <Skeleton className="h-mdx-textarea-min" />
        )}
      </div>

      {error ? <Callout>{error}</Callout> : null}

      {bench ? (
        <>
          <Histogram bench={bench} />
          <Evidence bench={bench} />
          <p className="m-0 text-mdx-13 text-mdx-slate">
            {methodLine(bench)}
            {pending ? ' Re-sampling…' : ''}
          </p>
        </>
      ) : null}
    </section>
  )
}

/**
 * The method line — "Based on 10,000 sampled combinations, following your
 * branching rules."
 *
 * It states WHICH method ran, because the two are different promises: exact
 * enumeration is a fact about every reachable combination, and Monte Carlo is
 * an estimate. An operator reading "18%" deserves to know which one they have.
 */
function methodLine(bench: Bench): string {
  const count = bench.samples.toLocaleString('en-GB')
  return bench.method === 'exact'
    ? `Based on all ${count} reachable answer combinations, following your branching rules.`
    : `Based on ${count} sampled combinations, following your branching rules.`
}

/* ── the histogram ──────────────────────────────────────────────────────── */

function Histogram({ bench }: { bench: Bench }) {
  const { histogram, distribution } = bench
  const tallest = histogram.reduce((max, bucket) => Math.max(max, bucket.count), 0)

  /**
   * A band nothing reached gets its own REGION rather than a run of empty
   * bars — Story 7.2's "an unreachable band is named explicitly rather than
   * shown as an empty bucket". A reader should not have to notice an absence.
   */
  const unreached = [...bench.bands].sort((a, b) => b.min - a.min).find((band) => !band.reached)

  if (!distribution || histogram.length === 0) {
    return (
      <p className="m-0 text-mdx-15 text-mdx-slate">
        No answer combination gets through your branching rules yet, so there is nothing to plot.
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-mdx-10">
      {/*
        The export draws the plot 130px tall. `--mdx-row-identity-sm` IS 130px,
        under a name that means a row's flex basis — borrowed for the number,
        not the role, because the scale has no chart height and the census does
        not count `height` (Q-050, Q-052).
      */}
      <div
        role="img"
        aria-label={chartLabel(bench)}
        className="flex h-mdx-row-identity-sm items-end gap-mdx-3"
      >
        {histogram.map((bucket, index) => (
          <Bar key={index} bucket={bucket} tallest={tallest} />
        ))}

        {unreached ? (
          // `flex: 11 1 0%` exactly as the export sets it — a basis of zero, so
          // the region takes its share of the row rather than a fixed width
          // plus a share, which crowds the bars into the left third.
          <span
            className="flex items-end justify-center self-stretch rounded-tr-mdx-5 border-l border-dashed border-mdx-line-strong bg-mdx-hot-tint px-mdx-6 pb-mdx-8 text-center"
            style={{ flex: '11 1 0%' }}
          >
            {/* The export sets 1.35 here; the scale runs 1.16 → 1.55 with
                nothing between, so this takes the surface's own leading. */}
            <span className="text-mdx-13 text-mdx-slate">
              {unreached.label} band
              <br />
              nothing lands here yet
            </span>
          </span>
        ) : null}
      </div>

      <div className="flex items-baseline gap-mdx-12 text-mdx-13 text-mdx-slate">
        <span className="flex-none tabular-nums">{distribution.min}</span>
        <span className="flex-1 text-center font-mdx-semibold text-mdx-ink">
          most land here — {distribution.p25} to {distribution.p75}
        </span>
        <span className="flex-none tabular-nums">{distribution.max}</span>
      </div>
    </div>
  )
}

function Bar({ bucket, tallest }: { bucket: HistogramBucket; tallest: number }) {
  // A proportion of the tallest bar — data, not a token, the same way the
  // funnels list's band bar is a proportion of its total.
  const height = tallest === 0 ? 0 : Math.max(2, Math.round((bucket.count / tallest) * 100))

  return (
    <span
      className={[
        'min-h-mdx-2 flex-1 rounded-t-mdx-5',
        bucket.tone ? BAR_TONE[bucket.tone] : 'bg-mdx-line',
      ].join(' ')}
      style={{ height: `${height}%` }}
    />
  )
}

/** What a screen reader is told the chart shows — the shape, not the bars. */
function chartLabel(bench: Bench): string {
  if (!bench.distribution) return 'No reachable answer combinations to plot.'
  const unreached = bench.bands.filter((band) => !band.reached).map((band) => band.label)
  const tail = unreached.length > 0 ? ` None reach ${unreached.join(' or ')}.` : ''
  return (
    `Score distribution across sampled answer combinations: ` +
    `most land between ${bench.distribution.p25} and ${bench.distribution.p75}.${tail}`
  )
}

/* ── the evidence rows ──────────────────────────────────────────────────── */

function Evidence({ bench }: { bench: Bench }) {
  const { distribution, reachable, unknown } = bench

  return (
    <div className="flex flex-col gap-mdx-8">
      {reachable ? (
        <Row mark="↔" tone="range">
          Your rubric can produce scores from {reachable.min} to {reachable.max}.
        </Row>
      ) : null}

      {distribution ? (
        <Row mark="◐" tone="spread">
          Half of all reachable answer combinations land between {distribution.p25} and{' '}
          {distribution.p75}.
        </Row>
      ) : null}

      {/*
        PRD §3.18: a criterion sourced from `extracted` or `transcript` "is
        unknown at calibration time and must be shown as a **stated range**, not
        silently treated as zero" — and since Story 20.9, `derived`. So the row
        names the criterion, says why it is unknown, and gives the span it could
        add.
      */}
      {unknown.criteria.map((criterion) => (
        <Row key={criterion.criterion_id} mark="?" tone="unknown">
          {unknownSentence(criterion, bench)}
        </Row>
      ))}
    </div>
  )
}

function unknownSentence(criterion: UnknownCriterion, bench: Bench): string {
  const why =
    criterion.source === 'extracted'
      ? `${criterion.criterion_label} is extracted and unknown until someone writes something`
      : criterion.source === 'derived'
        ? // Story 20.9: the ICP fit, which no answer set alone can predict.
          DERIVED_COMPOSED.benchWhy(criterion.criterion_label)
        : `${criterion.criterion_label} comes from a call and is unknown until one happens`
  // A criterion that can award nothing says so. Story 20.3's door writes one
  // until its points can be set (Q-103), and "between 0 and 0 points" is a true
  // sentence that reads as a broken one.
  const span =
    criterion.max === 0
      ? 'it adds no points yet'
      : `it adds between ${criterion.min} and ${criterion.max} points`

  const blocked = topBandBlockedBy(bench)
  return blocked
    ? `No combination reaches your ${blocked.label} band on answers alone — ${why}, and ${span}.`
    : `${why[0]!.toUpperCase()}${why.slice(1)}, so ${span}.`
}

/** The band that only the unknowns can reach, if there is one. */
function topBandBlockedBy(bench: Bench): BandShare | undefined {
  return [...bench.bands]
    .sort((a, b) => b.min - a.min)
    .find((band) => !band.reached && band.reachable_with_unknowns)
}

function Row({
  mark,
  tone,
  children,
}: {
  mark: string
  tone: keyof typeof MARK_TONE
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-mdx-16 rounded-mdx-control bg-mdx-ground px-mdx-16 py-mdx-12">
      <span
        aria-hidden="true"
        className={`flex size-mdx-evidence-icon flex-none items-center justify-center rounded-mdx-control-sm text-mdx-14 font-mdx-bold ${MARK_TONE[tone]}`}
      >
        {mark}
      </span>
      <span className="min-w-0 text-mdx-15">{children}</span>
    </div>
  )
}
